import { Data, Effect, Option } from 'effect';
import type { SessionInfo } from './get-session';

/**
 * Result of a session check. `Unavailable` means the check itself failed —
 * the auth API errored or was unreachable — and says nothing about whether
 * the user is signed in. Route guards must not treat it as signed-out: a
 * guard that ejects on `Unavailable` turns any infra blip into a forced
 * logout. Guards on pages that render fine for anonymous users fail open
 * (render, let the client re-check); only privileged UI (admin) fails
 * closed.
 */
export type SessionCheck = Data.TaggedEnum<{
  Authenticated: { readonly session: SessionInfo };
  Unauthenticated: {};
  Unavailable: {};
}>;
export const SessionCheck = Data.taggedEnum<SessionCheck>();

// Shared by both halves of the isomorphic session check: the client program
// in get-session.ts and the SSR program in get-session.server.ts ask
// different better-auth clients the same question and must map the answer
// identically. Pinned by test/unit/session-check-mapping.spec.ts — the
// 2026-08-20 funnel loss was this mapping collapsing an errored check into
// "signed out".

/** The session check never got an answer (network failure, thrown fetch). */
export class SessionApiUnreachable extends Data.TaggedError(
  'SessionApiUnreachable',
)<{
  readonly cause: unknown;
}> {}

/** The auth API answered the session check with an error status. */
export class SessionApiError extends Data.TaggedError('SessionApiError')<{
  readonly status: number;
  readonly statusText: string;
}> {}

/** Structural minimum of a better-auth getSession response, browser or SSR. */
export type AuthSessionResponse = {
  data: {
    user: { id: string; email: string; role?: string | null | undefined };
  } | null;
  error: { status: number; statusText: string } | null;
};

export const toSessionInfo = (user: {
  id: string;
  email: string;
  role?: string | null | undefined;
}): SessionInfo => ({
  userId: user.id,
  email: user.email,
  role: user.role ?? null,
});

/**
 * Ask a better-auth client for the session. `Option.none` is a definitive
 * "no session"; every failure stays in the error channel, never collapsed
 * into a null that reads as signed-out.
 */
export const remoteSessionCheck = (
  call: () => Promise<AuthSessionResponse>,
): Effect.Effect<
  Option.Option<SessionInfo>,
  SessionApiError | SessionApiUnreachable
> =>
  Effect.tryPromise({
    try: call,
    catch: (cause) => new SessionApiUnreachable({ cause }),
  }).pipe(
    Effect.flatMap(({ data, error }) =>
      error !== null
        ? Effect.fail(
            new SessionApiError({
              status: error.status,
              statusText: error.statusText,
            }),
          )
        : Effect.succeed(
            data?.user ? Option.some(toSessionInfo(data.user)) : Option.none(),
          ),
    ),
  );

export const toSessionCheck: (
  session: Option.Option<SessionInfo>,
) => SessionCheck = Option.match({
  onSome: (session): SessionCheck => SessionCheck.Authenticated({ session }),
  onNone: (): SessionCheck => SessionCheck.Unauthenticated(),
});

/**
 * A failed check becomes `Unavailable` — logged, never thrown and never
 * `Unauthenticated` — so route guards can fail open on infra trouble.
 */
export const orUnavailable = <E>(
  program: Effect.Effect<SessionCheck, E>,
): Effect.Effect<SessionCheck> =>
  program.pipe(
    Effect.catch((error) =>
      Effect.as(
        Effect.logWarning('Session check failed').pipe(
          Effect.annotateLogs({ error }),
        ),
        SessionCheck.Unavailable(),
      ),
    ),
  );
